import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { NextPage } from '../components/NextPage';
import { BookIcon, VolumeIcon, CheckCircleIcon, PlayIcon, PauseIcon, LightbulbIcon, MessageSquareIcon, StarIcon, BrainIcon, PenIcon, HeadphonesIcon, ClockIcon, XIcon } from 'lucide-react';
const LESSON_DATA = {
  id: 3,
  title: {
    en: 'Asking Questions',
    ar: 'طرح الأسئلة'
  },
  description: {
    en: 'Learn how to form questions in English and get the information you need',
    ar: 'تعلم كيفية تكوين الأسئلة باللغة الإنجليزية والحصول على المعلومات التي تحتاجها'
  },
  duration: '35 minutes',
  durationAr: '35 دقيقة',
  sections: [{
    id: 'wh-questions',
    title: {
      en: 'WH Questions',
      ar: 'أسئلة WH'
    },
    phrases: [{
      en: 'What is your name?',
      ar: 'ما اسمك؟',
      context: 'Asking about identity',
      contextAr: 'السؤال عن الهوية'
    }, {
      en: 'Where do you live?',
      ar: 'أين تسكن؟',
      context: 'Asking about location',
      contextAr: 'السؤال عن المكان'
    }, {
      en: 'When is your birthday?',
      ar: 'متى عيد ميلادك؟',
      context: 'Asking about time',
      contextAr: 'السؤال عن الوقت'
    }, {
      en: 'Why are you learning English?',
      ar: 'لماذا تتعلم الإنجليزية؟',
      context: 'Asking about reason',
      contextAr: 'السؤال عن السبب'
    }, {
      en: 'How do you spell that?',
      ar: 'كيف تتهجى ذلك؟',
      context: 'Asking for spelling',
      contextAr: 'السؤال عن التهجئة'
    }]
  }, {
    id: 'yes-no-questions',
    title: {
      en: 'Yes/No Questions',
      ar: 'أسئلة نعم/لا'
    },
    phrases: [{
      en: 'Do you speak English?',
      ar: 'هل تتحدث الإنجليزية؟',
      context: 'Asking about ability',
      contextAr: 'السؤال عن القدرة'
    }, {
      en: 'Are you a student?',
      ar: 'هل أنت طالب؟',
      context: 'Asking about occupation',
      contextAr: 'السؤال عن المهنة'
    }, {
      en: 'Can you help me?',
      ar: 'هل يمكنك مساعدتي؟',
      context: 'Asking for help',
      contextAr: 'طلب المساعدة'
    }, {
      en: 'Is this your book?',
      ar: 'هل هذا كتابك؟',
      context: 'Asking about possession',
      contextAr: 'السؤال عن الملكية'
    }]
  }],
  practice: [{
    question: {
      en: 'Which question word do we use to ask about time?',
      ar: 'أي كلمة سؤال نستخدم للسؤال عن الوقت؟'
    },
    options: [{
      en: 'When',
      ar: 'متى',
      correct: true
    }, {
      en: 'Where',
      ar: 'أين',
      correct: false
    }, {
      en: 'What',
      ar: 'ماذا',
      correct: false
    }, {
      en: 'Why',
      ar: 'لماذا',
      correct: false
    }]
  }, {
    question: {
      en: 'How do you form a yes/no question?',
      ar: 'كيف تكوّن سؤال نعم/لا؟'
    },
    options: [{
      en: 'Start with Do/Does/Are/Is',
      ar: 'ابدأ بـ Do/Does/Are/Is',
      correct: true
    }, {
      en: 'Start with What/Where/When',
      ar: 'ابدأ بـ What/Where/When',
      correct: false
    }, {
      en: 'End with a period',
      ar: 'انتهِ بنقطة',
      correct: false
    }, {
      en: 'Start with You/He/She',
      ar: 'ابدأ بـ You/He/She',
      correct: false
    }]
  }],
  tips: [{
    en: 'Always use a rising intonation at the end of yes/no questions',
    ar: 'استخدم دائماً نبرة صاعدة في نهاية أسئلة نعم/لا'
  }, {
    en: 'Remember to invert the subject and verb in questions',
    ar: 'تذكر أن تعكس الفاعل والفعل في الأسئلة'
  }, {
    en: 'Practice asking questions in real conversations',
    ar: 'تدرب على طرح الأسئلة في محادثات حقيقية'
  }]
};
export function LessonThree() {
  const {
    isArabic
  } = useLanguage();
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const playAudio = (text: string) => {
    if (playingAudio === text) {
      window.speechSynthesis.cancel();
      setPlayingAudio(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.onend = () => setPlayingAudio(null);
    setPlayingAudio(text);
    window.speechSynthesis.speak(utterance);
  };
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };
  const checkAnswers = () => {
    setShowResults(true);
  };
  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };
  const getCorrectAnswers = () => {
    return LESSON_DATA.practice.reduce((count, question, index) => {
      const selectedAnswer = selectedAnswers[index];
      if (selectedAnswer !== undefined && question.options[selectedAnswer].correct) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3758">
      <Sitemap currentPage={{
      en: `Lesson ${LESSON_DATA.id}: ${LESSON_DATA.title.en}`,
      ar: `الدرس ${LESSON_DATA.id}: ${LESSON_DATA.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Course 1',
        ar: 'الدورة 1'
      },
      href: '/learn/course/1'
    }]} data-id="element-3759" />
      <div className="max-w-4xl mx-auto" data-id="element-3760">
        <div className="mb-12" data-id="element-3761">
          <div className="flex items-center gap-4 mb-6" data-id="element-3762">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3763">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3764" />
            </div>
            <div data-id="element-3765">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3766">
                {isArabic ? LESSON_DATA.title.ar : LESSON_DATA.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3767">
                {isArabic ? LESSON_DATA.description.ar : LESSON_DATA.description.en}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6" data-id="element-3768">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3769">
              <ClockIcon className="w-5 h-5" data-id="element-3770" />
              <span data-id="element-3771">
                {isArabic ? LESSON_DATA.durationAr : LESSON_DATA.duration}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3772">
              <BrainIcon className="w-5 h-5" data-id="element-3773" />
              <span data-id="element-3774">
                {LESSON_DATA.sections.length} {isArabic ? 'أقسام' : 'sections'}
              </span>
            </div>
          </div>
        </div>
        {LESSON_DATA.sections.map(section => <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3775">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3776">
              {isArabic ? section.title.ar : section.title.en}
            </h2>
            <div className="space-y-4" data-id="element-3777">
              {section.phrases.map((phrase, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-3778">
                  <div className="flex items-center justify-between mb-2" data-id="element-3779">
                    <div data-id="element-3780">
                      <div className="font-medium text-gray-900 dark:text-white text-lg" data-id="element-3781">
                        {phrase.en}
                      </div>
                      <div className="text-blue-600 dark:text-blue-400 text-lg" dir="rtl" data-id="element-3782">
                        {phrase.ar}
                      </div>
                    </div>
                    <button onClick={() => playAudio(phrase.en)} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-3783">
                      {playingAudio === phrase.en ? <PauseIcon className="w-5 h-5" data-id="element-3784" /> : <VolumeIcon className="w-5 h-5" data-id="element-3785" />}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3786">
                    {isArabic ? phrase.contextAr : phrase.context}
                  </div>
                </div>)}
            </div>
          </div>)}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3787">
          <div className="flex items-center gap-3 mb-6" data-id="element-3788">
            <PenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3789" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3790">
              {isArabic ? 'تمارين' : 'Practice'}
            </h2>
          </div>
          <div className="space-y-8" data-id="element-3791">
            {LESSON_DATA.practice.map((question, qIndex) => <div key={qIndex} className="space-y-4" data-id="element-3792">
                <p className="text-lg text-gray-900 dark:text-white" data-id="element-3793">
                  {isArabic ? question.question.ar : question.question.en}
                </p>
                <div className="grid grid-cols-1 gap-3" data-id="element-3794">
                  {question.options.map((option, oIndex) => <button key={oIndex} onClick={() => handleAnswerSelect(qIndex, oIndex)} className={`
                        p-4 rounded-lg text-left transition-colors
                        ${selectedAnswers[qIndex] === oIndex ? showResults ? option.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'}
                      `} data-id="element-3795">
                      <div className="flex justify-between items-center" data-id="element-3796">
                        <div data-id="element-3797">
                          <div className="font-medium" data-id="element-3798">{option.en}</div>
                          <div className="text-sm opacity-75" data-id="element-3799">{option.ar}</div>
                        </div>
                        {showResults && (selectedAnswers[qIndex] === oIndex ? option.correct ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3800" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-3801" /> : null)}
                      </div>
                    </button>)}
                </div>
              </div>)}
          </div>
          <div className="mt-6" data-id="element-3802">
            {showResults ? <div data-id="element-3803">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4" data-id="element-3804">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-3805">
                    {isArabic ? 'نتائجك' : 'Your Results'}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-3806">
                    {isArabic ? `لقد أجبت على ${getCorrectAnswers()} من ${LESSON_DATA.practice.length} بشكل صحيح` : `You got ${getCorrectAnswers()} out of ${LESSON_DATA.practice.length} correct`}
                  </p>
                </div>
                <button onClick={resetPractice} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3807">
                  {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
                </button>
              </div> : <button onClick={checkAnswers} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" disabled={Object.keys(selectedAnswers).length < LESSON_DATA.practice.length} data-id="element-3808">
                {isArabic ? 'تحقق من إجاباتك' : 'Check Answers'}
              </button>}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 mb-8" data-id="element-3809">
          <div className="flex items-center gap-3 mb-6" data-id="element-3810">
            <LightbulbIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3811" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3812">
              {isArabic ? 'نصائح مفيدة' : 'Helpful Tips'}
            </h2>
          </div>
          <ul className="space-y-4" data-id="element-3813">
            {LESSON_DATA.tips.map((tip, index) => <li key={index} className="flex items-start gap-3" data-id="element-3814">
                <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-3815" />
                <span className="text-gray-800 dark:text-gray-200" data-id="element-3816">
                  {isArabic ? tip.ar : tip.en}
                </span>
              </li>)}
          </ul>
        </div>
        <NextPage currentPage={LESSON_DATA.id} totalPages={5} nextPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id + 1}`} prevPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id - 1}`} nextTitle={{
        en: 'Common Phrases',
        ar: 'العبارات الشائعة'
      }} nextDescription={{
        en: 'Learn everyday expressions and common phrases in English',
        ar: 'تعلم التعبيرات اليومية والعبارات الشائعة باللغة الإنجليزية'
      }} data-id="element-3817" />
      </div>
    </div>;
}